import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@rudi/ui/select';
import { ChevronLeft, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader, QueryState } from '../components/page';
import { useClearUserFeature, useSetUserFeature, useUserFeatures, useUsers } from '../api/hooks';
import type { FeatureSource, UserFeatureState } from '../api/types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@rudi/ui/tabs';
import {
  UserAdditionalEmailsPanel,
  UserInternalPanel,
  UserProfilePanel,
  UserRolesPanel,
  UserSecurityPanel,
  userAffiliations,
} from './UserSettingsPanels';
import { DeleteUserDialog } from './DeleteUserDialog';
import { AccessRequestCard } from './AccessRequestCard';

type OverrideState = 'inherit' | 'on' | 'off';

function overrideState(feature: UserFeatureState): OverrideState {
  if (feature.override === null) return 'inherit';
  return feature.override ? 'on' : 'off';
}

const SOURCE_LABELS: Record<FeatureSource, string> = {
  default: 'default',
  platform: 'environment',
  tenant_legacy: 'tenant (legacy)',
  tenant: 'tenant',
  role: 'role',
  user: 'user',
};

function UserApplicationsPanel({ userId }: { userId: string }) {
  const { data, isLoading, error } = useUserFeatures(userId);
  const setFeature = useSetUserFeature();
  const clearFeature = useClearUserFeature();
  const onChange = async (feature: UserFeatureState, value: OverrideState) => {
    try {
      if (value === 'inherit') {
        await clearFeature.mutateAsync({ userId, key: feature.key });
      } else {
        await setFeature.mutateAsync({ userId, key: feature.key, enabled: value === 'on' });
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Update failed');
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Review application access and features. Inherit follows tenant, role, and environment
        settings.
      </p>
      <QueryState isLoading={isLoading} error={error} empty={data?.features.length === 0}>
        <div className="rounded-3xl border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Application or feature</TableHead>
                <TableHead>Effective</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>User override</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.features.map(f => (
                <TableRow key={f.key}>
                  <TableCell>
                    <span className="font-medium">{f.label}</span>
                    <span className="ml-2 font-mono text-xs text-muted-foreground">{f.key}</span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={f.effective ? 'default' : 'secondary'}>
                      {f.effective ? 'ON' : 'OFF'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={f.source === 'default' ? 'outline' : 'secondary'}>
                      {SOURCE_LABELS[f.source]}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Select
                      disabled={setFeature.isPending || clearFeature.isPending}
                      value={overrideState(f)}
                      onValueChange={v => onChange(f, v as OverrideState)}
                    >
                      <SelectTrigger className="w-36" aria-label={`Override ${f.label}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="inherit">Inherit</SelectItem>
                        <SelectItem value="on">Override ON</SelectItem>
                        <SelectItem value="off">Override OFF</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </QueryState>
    </div>
  );
}

export function UserDetailPage() {
  const { userId } = useParams({ from: '/users/$userId' });
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const users = useUsers();
  const user = users.data?.users.find(item => item.id === userId);
  const title = user?.display_name || user?.email || user?.username || 'User';

  return (
    <div>
      <Link
        to="/users"
        className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" /> Users
      </Link>
      <QueryState isLoading={users.isLoading} error={users.error}>
        {user ? (
          <>
            <PageHeader
              title={title}
              description={user.email ?? user.username}
              actions={
                <Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete user
                </Button>
              }
            />
            <div className="mb-5 flex flex-wrap gap-3 text-sm text-muted-foreground">
              {user.is_internal && <Badge variant="outline">Internal</Badge>}
              {userAffiliations(user).map(affiliation => (
                <div key={affiliation.tenant_id} className="flex flex-wrap items-center gap-2">
                  <Link
                    to="/tenants/$tenantId"
                    params={{ tenantId: affiliation.tenant_id }}
                    className="hover:underline"
                  >
                    {affiliation.tenant_name}
                  </Link>
                  {affiliation.teams.map(team => (
                    <Link
                      key={team.id}
                      to="/tenants/$tenantId/teams/$teamId"
                      params={{ tenantId: affiliation.tenant_id, teamId: team.id }}
                      className="underline underline-offset-4"
                    >
                      {team.name}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
            <AccessRequestCard userId={userId} />
            <Tabs key={userId} defaultValue="profile">
              <TabsList aria-label="User settings" className="mb-5 h-auto flex-wrap">
                <TabsTrigger value="profile">Profile</TabsTrigger>
                <TabsTrigger value="applications">Applications</TabsTrigger>
                <TabsTrigger value="roles">Roles</TabsTrigger>
                <TabsTrigger value="security">Security</TabsTrigger>
              </TabsList>
              <TabsContent value="profile" className="space-y-5">
                <UserProfilePanel user={user} />
                <UserInternalPanel user={user} />
                <UserAdditionalEmailsPanel user={user} />
              </TabsContent>
              <TabsContent value="applications">
                <UserApplicationsPanel userId={userId} />
              </TabsContent>
              <TabsContent value="roles">
                <UserRolesPanel user={user} />
              </TabsContent>
              <TabsContent value="security">
                <UserSecurityPanel user={user} />
              </TabsContent>
            </Tabs>
          </>
        ) : (
          <p role="alert">This user was not found. Return to Users to choose another account.</p>
        )}
      </QueryState>
      <DeleteUserDialog
        user={user ?? null}
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => navigate({ to: '/users' })}
      />
    </div>
  );
}
